import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
(function injectNavStyles() {
  if (typeof document === 'undefined' || document.getElementById('ur-nav-styles')) return;
  const s = document.createElement('style');
  s.id = 'ur-nav-styles';
  s.textContent = `
    @keyframes navSlideDown { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
    @keyframes mobileMenuIn { from { opacity:0; transform:translateY(-10px) scale(0.98); } to { opacity:1; transform:translateY(0) scale(1); } }
    .ur-nav-link:hover { color: #60a5fa !important; }
    .ur-dropdown-item:hover { background: linear-gradient(135deg,#3b82f6,#8b5cf6) !important; color:#fff !important; }
    .ur-mobile-item:hover { background: rgba(255,255,255,0.1) !important; }
  `;
  document.head.appendChild(s);
})();

function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);


  const toggle = (name) => setOpenMenu(prev => prev === name ? null : name);

  const hideNavbarPaths = [
    '/login', '/admin-login', '/authority-login',
    '/register/student', '/register/faculty',
    '/forgot-password', '/reset-password'
  ];
  if (hideNavbarPaths.includes(location.pathname)) return null;

  // When user is logged in and inside dashboard, Navbar is minimal
  // NotificationBell + theme toggle live in the Topbar — don't duplicate
  const isLoggedIn = !!user;

  const handleLogout = () => { logout(); navigate('/'); setMobileOpen(false); };
  const handleNameClick = () => {
    if (user?.role === 'authority') navigate('/authority/dashboard');
    else if (user?.role === 'admin' || user?.role === 'super_admin') navigate('/admin/dashboard');
    else navigate('/dashboard');
    setMobileOpen(false);
  };

  const loginOptions = [
    { path: '/login/student',   label: 'Student Login',   icon: '🎓' },
    { path: '/login/faculty',   label: 'Faculty Login',   icon: '👨‍🏫' },
    { path: '/admin-login',     label: 'Admin Login',     icon: '🛡️' },
    { path: '/authority-login', label: 'Authority Login', icon: '⚖️' },
  ];
  const registerOptions = [
    { path: '/register/student', label: 'Student Register', icon: '🎓' },
    { path: '/register/faculty', label: 'Faculty Register', icon: '👨‍🏫' },
  ];

  const dropMenu = {
    position: 'absolute', top: 'calc(100% + 6px)', left: '50%',
    transform: 'translateX(-50%)', minWidth: 210,
    background: 'linear-gradient(145deg,#1e293b,#0f172a)',
    borderRadius: 12, padding: 6,
    boxShadow: '0 16px 40px rgba(0,0,0,0.5)', border: '1px solid rgba(99,102,241,0.25)',
    animation: 'navSlideDown 0.18s ease-out', zIndex: 2000,
  };

  const dropItem = {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '11px 14px', color: '#e2e8f0', textDecoration: 'none',
    fontSize: 14, borderRadius: 8, transition: 'all 0.15s', cursor: 'pointer',
  };

  const navBtn = (active) => ({
    color: active ? '#818cf8' : '#e2e8f0',
    border: 'none', fontSize: 15, fontWeight: 500, cursor: 'pointer',
    padding: '8px 14px', borderRadius: 8,
    display: 'flex', alignItems: 'center', gap: 5,
    background: active ? 'rgba(99,102,241,0.15)' : 'transparent',
    transition: 'all 0.2s',
  });

  return (
    <>
      <nav ref={navRef} style={{
        position: 'sticky', top: 0, zIndex: 1000,
        background: 'linear-gradient(135deg,#1e293b 0%,#0f172a 100%)',
        borderBottom: '1px solid rgba(59,130,246,0.2)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      }}>
        <div style={{
          maxWidth: 1280, margin: '0 auto', padding: '0 20px',
          height: 64, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 16,
        }}>

          {/* ── LEFT: Logo ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flexShrink: 0 }}
            onClick={() => navigate('/')}>
            <div style={{
              width: 38, height: 38, borderRadius: 10,
              background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, flexShrink: 0,
            }}>🎓</div>
            <span style={{
              fontSize: 20, fontWeight: 800,
              background: 'linear-gradient(135deg,#60a5fa,#c084fc)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.5px',
            }}>UniResolve</span>
          </div>

          {/* ── CENTER: Nav links (desktop only) ── */}
          <div className="ur-desktop-nav" style={{
            display: 'flex', alignItems: 'center', gap: 4,
            flex: 1, justifyContent: 'center',
          }}>
            <Link to="/" className="ur-nav-link" style={{
              color: '#e2e8f0', textDecoration: 'none',
              fontSize: 15, fontWeight: 500, padding: '8px 14px', borderRadius: 8,
              transition: 'color 0.2s',
            }}>Home</Link>

            {!isLoggedIn && (
              <>
                {/* Login dropdown */}
                <div style={{ position: 'relative' }}>
                  <button style={navBtn(openMenu === 'login')} onClick={() => toggle('login')}>
                    Login
                    <span style={{ fontSize: 9, transition: 'transform 0.2s', transform: openMenu === 'login' ? 'rotate(180deg)' : 'none', display: 'inline-block' }}>▼</span>
                  </button>
                  {openMenu === 'login' && (
                    <div style={dropMenu}>
                      {loginOptions.map(o => (
                        <Link key={o.path} to={o.path} className="ur-dropdown-item" style={dropItem}
                          onClick={() => setOpenMenu(null)}>
                          <span style={{ fontSize: 17, flexShrink: 0 }}>{o.icon}</span> {o.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Register dropdown */}
                <div style={{ position: 'relative' }}>
                  <button style={navBtn(openMenu === 'register')} onClick={() => toggle('register')}>
                    Register
                    <span style={{ fontSize: 9, transition: 'transform 0.2s', transform: openMenu === 'register' ? 'rotate(180deg)' : 'none', display: 'inline-block' }}>▼</span>
                  </button>
                  {openMenu === 'register' && (
                    <div style={dropMenu}>
                      {registerOptions.map(o => (
                        <Link key={o.path} to={o.path} className="ur-dropdown-item" style={dropItem}
                          onClick={() => setOpenMenu(null)}>
                          <span style={{ fontSize: 17, flexShrink: 0 }}>{o.icon}</span> {o.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>


              </>
            )}

            {isLoggedIn && (
              <>
                <span style={{
                  color: '#60a5fa', fontSize: 14, fontWeight: 600,
                  padding: '7px 14px', background: 'rgba(59,130,246,0.12)',
                  borderRadius: 20, cursor: 'pointer',
                }} onClick={handleNameClick}>{user.name}</span>

                <button style={{
                  padding: '7px 14px', background: 'transparent', color: '#e2e8f0',
                  border: '1px solid #334155', borderRadius: 8, fontSize: 14, cursor: 'pointer',
                }} onClick={handleLogout}
                  onMouseEnter={e => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#e2e8f0'; }}>
                  Logout
                </button>
              </>
            )}
          </div>

          {/* ── RIGHT: About dropdown (always far right) ── */}
          <div className="ur-desktop-nav" style={{ position: 'relative', flexShrink: 0 }}>
            <button style={navBtn(openMenu === 'about')} onClick={() => toggle('about')}>
              About
              <span style={{ fontSize: 9, transition: 'transform 0.2s', transform: openMenu === 'about' ? 'rotate(180deg)' : 'none', display: 'inline-block' }}>▼</span>
            </button>
            {openMenu === 'about' && (
              <div style={{ ...dropMenu, left: 'auto', right: 0, transform: 'none', width: 300 }}>
                <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg,#6366f1,#a78bfa)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: '#fff', fontWeight: 800 }}>U</div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800, background: 'linear-gradient(135deg,#818cf8,#22d3ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>UniResolve</div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Complaint Management System</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>
                    A transparent platform where every complaint is tracked and every voice is heard.
                  </div>
                </div>
                <div style={{ padding: '6px 0' }}>
                  {[
                    { icon: '✉', label: 'Email', value: 'uniresolve.noreply@gmail.com', color: '#818cf8' },
                    { icon: '☎', label: 'Phone', value: '0335 2448504', color: '#a78bfa' },
                    { icon: '⌖', label: 'Location', value: 'Hyderabad, Pakistan', color: '#22d3ee' },
                  ].map(item => (
                    <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px' }}>
                      <div style={{ width: 28, height: 28, borderRadius: 7, background: item.color + '18', border: '1px solid ' + item.color + '30', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: item.color, flexShrink: 0 }}>{item.icon}</div>
                      <div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 1 }}>{item.label}</div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ padding: '8px 16px', borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.2)', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>© 2026 UniResolve</span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>All rights reserved</span>
                </div>
              </div>
            )}
          </div>

          {/* ── MOBILE right side ── */}
          <div className="ur-mobile-nav" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

            <button onClick={() => setMobileOpen(o => !o)} style={{
              width: 36, height: 36, borderRadius: 9,
              background: mobileOpen ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.1)',
              border: '1px solid ' + (mobileOpen ? 'rgba(99,102,241,0.5)' : 'rgba(255,255,255,0.2)'),
              color: '#fff', fontSize: 18, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {mobileOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {/* ── Mobile menu ── */}
        {mobileOpen && (
          <div style={{
            position: 'absolute', top: 64, left: 0, right: 0,
            background: 'linear-gradient(145deg,#0f172a,#1e1b4b)',
            borderBottom: '1px solid rgba(99,102,241,0.25)',
            boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
            animation: 'mobileMenuIn 0.22s ease-out',
            zIndex: 999, padding: '12px 16px 20px',
          }}>
            {isLoggedIn && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 14px', marginBottom: 10,
                background: 'rgba(99,102,241,0.12)', borderRadius: 12,
                border: '1px solid rgba(99,102,241,0.25)',
              }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 800, color: '#fff', flexShrink: 0,
                }}>{user.name?.[0]?.toUpperCase() || 'U'}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{user.name}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 1 }}>
                    {user.role?.replace('_', ' ')} {user.department ? '· ' + user.department : ''}
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Link to="/" className="ur-mobile-item" style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
                color: '#e2e8f0', textDecoration: 'none', fontSize: 14, fontWeight: 500,
                borderRadius: 10,
              }} onClick={() => setMobileOpen(false)}>
                🏠 Home
              </Link>

              {!isLoggedIn && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1, padding: '10px 14px 4px' }}>Login</div>
                  {loginOptions.map(o => (
                    <Link key={o.path} to={o.path} className="ur-mobile-item" style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
                      color: '#e2e8f0', textDecoration: 'none', fontSize: 14, borderRadius: 10,
                    }} onClick={() => setMobileOpen(false)}>
                      <span style={{ fontSize: 18 }}>{o.icon}</span> {o.label}
                    </Link>
                  ))}
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1, padding: '10px 14px 4px' }}>Register</div>
                  {registerOptions.map(o => (
                    <Link key={o.path} to={o.path} className="ur-mobile-item" style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
                      color: '#e2e8f0', textDecoration: 'none', fontSize: 14, borderRadius: 10,
                    }} onClick={() => setMobileOpen(false)}>
                      <span style={{ fontSize: 18 }}>{o.icon}</span> {o.label}
                    </Link>
                  ))}
                </>
              )}

              {isLoggedIn && (
                <>
                  <div className="ur-mobile-item" style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
                    color: '#60a5fa', fontSize: 14, fontWeight: 600, borderRadius: 10, cursor: 'pointer',
                  }} onClick={handleNameClick}>
                    📊 Dashboard
                  </div>
                  <div className="ur-mobile-item" style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
                    color: '#fca5a5', fontSize: 14, fontWeight: 600, borderRadius: 10, cursor: 'pointer',
                  }} onClick={handleLogout}>
                    🚪 Logout
                  </div>
                </>
              )}

              <div style={{ marginTop: 8, padding: '12px 14px', background: 'rgba(255,255,255,0.04)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>About</div>
                {[
                  { icon: '✉', value: 'uniresolve.noreply@gmail.com' },
                  { icon: '☎', value: '0335 2448504' },
                  { icon: '⌖', value: 'Hyderabad, Pakistan' },
                ].map(item => (
                  <div key={item.value} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                    <span style={{ fontSize: 14, width: 20, textAlign: 'center' }}>{item.icon}</span>
                    {item.value}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>

      <style>{`
        @media (min-width: 769px) { .ur-mobile-nav { display: none !important; } }
        @media (max-width: 768px) { .ur-desktop-nav { display: none !important; } }
      `}</style>
    </>
  );
}

export default Navbar;
